import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useTranslation from 'Hooks/useTranslation';
import useDebounce from 'Hooks/useDebounce';
import { getCourses, getDropdownsInformation, createCourse } from 'Api/courses-api';
import type { AdminDropdownsI } from 'Api/courses-api';
import { s3 } from 'Services/AWS/s3';
import { colors } from 'Constants/Colors';
import type { AdminCourseI, AdminCourseCreateI } from 'types';

const DEBOUNCE_MS = 500;
const CHIP_COLORS = [colors.GREEN, colors.RED, colors.PURPLE, colors.YELLOW, colors.BLUE, colors.PINK, colors.BROWN];
const EMPTY_DROPDOWNS: AdminDropdownsI = { categories: [], authors: [], terminals: [] };
const PUBLISH_ROUTE = '/cursos/editar/publicar';

/** Lo que el card necesita, más el curso original para resolver clicks sin re-buscarlo. */
export interface AdminCourseCardVM {
  course: AdminCourseI;
  name: string;
  description: string | null;
  /** URL ya firmada (o null): la key cruda no sale del hook. */
  thumbnailUrl: string | null;
  category: { name: string; color?: string } | null;
  creator: { fullName: string; photo: string | null } | null;
  /** Etiqueta traducida del badge de borrador; null en cursos activos. */
  draftLabel: string | null;
}

/** Un bloque de cards: Activos primero, Borradores después. Vacías no se emiten. */
export interface AdminCourseCardSectionI {
  key: 'active' | 'draft';
  title: string;
  items: AdminCourseCardVM[];
}

export default function useCoursesPage() {
  const { t2, language } = useTranslation('Courses');
  const history = useHistory();
  const location = useLocation<{ isComingFrom?: string; successful?: boolean } | undefined>();

  const comingFromPublish = location?.state?.isComingFrom === PUBLISH_ROUTE;
  // Semántica legacy: cualquier state con `successful` presente cuenta como éxito.
  const arrivedSuccessful = location?.state?.successful !== undefined;

  const [data, setData] = useState<AdminCourseI[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dropdowns, setDropdowns] = useState<AdminDropdownsI>(EMPTY_DROPDOWNS);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [author, setAuthor] = useState('');
  const [terminal, setTerminal] = useState('');
  const [isDraft, setIsDraft] = useState<boolean | null>(null);
  const [isCard, setIsCard] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string | null>>({});

  const [successful, setSuccessful] = useState(arrivedSuccessful);
  const [courseEdited, setCourseEdited] = useState(comingFromPublish);
  const [open, setOpen] = useState(comingFromPublish);

  const debouncedName = useDebounce(name, DEBOUNCE_MS);

  // El state de publicar es de un solo uso: se limpia para que un refresh
  // no re-dispare la alerta (mismo history.replace() del legacy).
  useEffect(() => {
    if (comingFromPublish) history.replace({ ...location, state: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isMounted = true;
    getDropdownsInformation().then(response => {
      if (isMounted) setDropdowns(response.data);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setError(null);

    // Se conserva la lista anterior mientras llega la nueva (sin flash de loading)
    getCourses({
      name: debouncedName,
      category: category || null,
      author: author || null,
      terminal: terminal || null,
      isDraft,
    }).then(response => {
      if (!isMounted) return;
      if (response.error !== null) {
        setError(response.error);
        return;
      }
      setData(response.data);
    });

    return () => {
      isMounted = false;
    };
  }, [debouncedName, category, author, terminal, isDraft, reloadToken]);

  const courses = useMemo(() => data ?? [], [data]);

  // Firmado en lote: la key S3 cruda se cambia por URL firmada aquí, nunca en el card.
  useEffect(() => {
    let isMounted = true;
    const withKey = courses.filter(course => course.thumbnailKey);
    if (withKey.length === 0) {
      setThumbnailUrls({});
      return undefined;
    }

    Promise.all(
      withKey.map(course =>
        s3
          .getSignedUrl(course.thumbnailKey as string)
          .then((response: { signedUrl?: string } | null) => [course.id, response?.signedUrl ?? null] as const)
          .catch(() => [course.id, null] as const)
      )
    ).then(entries => {
      if (isMounted) setThumbnailUrls(Object.fromEntries(entries));
    });

    return () => {
      isMounted = false;
    };
  }, [courses]);

  const loading = data === null && error === null;
  const retry = () => setReloadToken(token => token + 1);

  const colorCategory = useMemo(() => {
    const map: Record<string, string> = {};
    dropdowns.categories.forEach((categoryName, index) => {
      map[categoryName] = CHIP_COLORS[index % CHIP_COLORS.length];
    });
    return map;
  }, [dropdowns.categories]);

  // El dropdown de estatus trabaja con labels traducidos; el booleano es asunto del hook.
  const statusOptions = [t2('Active') as string, t2('Draft') as string];
  const setStatusName = (statusName: string) => {
    if (statusName === t2('Active')) setIsDraft(false);
    else if (statusName === t2('Draft')) setIsDraft(true);
    else setIsDraft(null);
  };

  const cardItems: AdminCourseCardVM[] = useMemo(
    () =>
      courses.map(course => ({
        course,
        name: course.name,
        description: course.description,
        thumbnailUrl: thumbnailUrls[course.id] ?? null,
        category: course.category ? { name: course.category, color: colorCategory[course.category] } : null,
        creator: course.creator ? { fullName: course.creator.fullName ?? '', photo: course.creator.photo } : null,
        draftLabel: course.isDraft ? (t2('Draft') as string) : null,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [courses, thumbnailUrls, colorCategory, language]
  );

  const cardSections: AdminCourseCardSectionI[] = useMemo(
    () =>
      (
        [
          {
            key: 'active',
            title: t2('SectionActive') as string,
            items: cardItems.filter(item => !item.course.isDraft),
          },
          { key: 'draft', title: t2('SectionDrafts') as string, items: cardItems.filter(item => item.course.isDraft) },
        ] as AdminCourseCardSectionI[]
      ).filter(section => section.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cardItems, language]
  );

  const goToCourseDetails = (course: AdminCourseI) => {
    history.push({
      pathname: `/cursos/${course.id}/detalles`,
      state: { colorCategory: colorCategory[course.category ?? ''] },
    });
  };

  const goToEditCourse = (course: AdminCourseI) => {
    history.push({
      pathname: `/cursos/${course.id}/editar`,
      state: { isComingFrom: '/cursos' },
    });
  };

  const handleCreateCourse = async (course: AdminCourseCreateI) => {
    const response = await createCourse(course);
    if (response.error !== null || response.data === null) {
      setSuccessful(false);
      setOpen(true);
      return;
    }
    setData(current => [response.data as AdminCourseI, ...(current ?? [])]);
    setSuccessful(true);
    setCourseEdited(false);
    setOpen(true);
  };

  const alertMsg = ((): string => {
    if (!successful) return t2('NotSuccessful') as string;
    return (courseEdited ? t2('OnSuccessfullEdit') : t2('OnSuccessMessage')) as string;
  })();

  return {
    t2,
    loading,
    error,
    retry,
    courses,
    cardSections,
    categories: dropdowns.categories,
    authors: dropdowns.authors,
    terminals: dropdowns.terminals,
    statusOptions,
    colorCategory,
    isCard,
    setIsCard,
    setName,
    setCategory,
    setAuthor,
    setTerminal,
    setStatusName,
    goToCourseDetails,
    goToEditCourse,
    handleCreateCourse,
    successful,
    open,
    setOpen,
    alertMsg,
  };
}
